/* Het CRM-dashboard (/crm): wat nu aandacht vraagt en waar gewerkt wordt, in de stijl
 * van de projectlijst en -pagina. Compact, alles klikt door.
 *
 *   kop           begroeting en "ga naar project" (/)
 *   aandacht      opvolgen (ADR 0028), eigen taken, mijlpalen over datum, offertes bij de klant
 *   links         pijplijn per stadium, actieve projecten, recent in projecten
 *   rechts        opvolgen, mijn taken, live installaties, mijlpalen, Charging */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, CheckSquare, FileText, Flag, History, Mail, PhoneCall, StickyNote, Zap } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/hooks/useAuth';
import { useProjectStars } from '@/hooks/useProjectStars';
import { getStageConfig } from '@/components/crm/project/projectUtils';
import { cn } from '@/lib/utils';
import { ProjectZoeker } from '@/features/projectlijst/ProjectZoeker';
import { useSignalen } from '@/features/projectlijst/hooks';
import { activiteit, filterEnSorteer, relatief, STADIA, STANDAARD_FILTER, type LijstProject } from '@/features/projectlijst/data/lijst';
import type { Signalen } from '@/features/projectlijst/data/signalen';
import { useMijnOpvolging, useOpvolgTelling } from '@/features/opvolging/hooks';
import { OpvolgLijst } from '@/features/opvolging/ui/OpvolgLijst';
import { groepeer, puntDag, lokaleDag as opvolgDag, type Opvolgpunt, type ProjectKop } from '@/features/opvolging/data/opvolging';
import type { Aandacht as AandachtData, DashboardProject, LiveLocatie, Mijlpaal, RecentItem, Taak } from './data/dashboard';
import {
  useAandacht, useCharging, useDashboardProjecten, useLive, useMijlpalen, useMijnTaken, useRecent, useRondTaakAf,
} from './hooks';

const EURO = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const euro = (v: number | null | undefined) => (v ? EURO.format(v) : '—');
const euroKort = (v: number) =>
  v >= 1_000_000 ? `€ ${(v / 1_000_000).toLocaleString('nl-NL', { maximumFractionDigits: 1 })} mln` : v >= 1000 ? `€ ${Math.round(v / 1000)}k` : v ? EURO.format(v) : '€ 0';
const STIP: Record<string, string> = { verkennen: 'bg-blue-600', voorstel: 'bg-purple-600', gewonnen: 'bg-green-600', operatie: 'bg-emerald-700' };
const DATUM = new Intl.DateTimeFormat('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const DAG_KORT = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short' });

/** Vandaag als YYYY-MM-DD in de tijdzone van de browser (niet UTC). */
const lokaleDag = (d: Date) => new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
/** Een datum ('2026-10-02') of tijdstempel ('2026-02-11 23:00:00+00') als lokale dag. */
const alsDag = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : lokaleDag(new Date(iso.replace(' ', 'T'))));
const middag = (dag: string) => new Date(`${dag}T12:00:00`);
const dagenVerschil = (iso: string, nu = new Date()) => Math.round((middag(alsDag(iso)).getTime() - middag(lokaleDag(nu)).getTime()) / 86_400_000);

function groet(nu = new Date()) {
  const u = nu.getHours();
  return u < 6 ? 'Goedenacht' : u < 12 ? 'Goedemorgen' : u < 18 ? 'Goedemiddag' : 'Goedenavond';
}

function Blok({ titel, id, rechts, children }: { titel: string; id: string; rechts?: ReactNode; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h2 id={id} className="text-lg font-semibold">{titel}</h2>
        {rechts}
      </div>
      {children}
    </section>
  );
}

function Verder({ naar, children }: { naar: string; children: ReactNode }) {
  return (
    <Link to={naar} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
      {children}
      <ArrowRight className="h-3.5 w-3.5" aria-hidden />
    </Link>
  );
}

/* ── aandacht ─────────────────────────────────────────────────────── */

function Tegel({ naar, icoon: Icoon, getal, label, onder, dringend }: {
  naar: string; icoon: typeof Mail; getal: number | undefined; label: string; onder?: string | null; dringend?: boolean;
}) {
  const leeg = !getal;
  const klasse = cn(
    'flex min-w-0 flex-col gap-1 rounded-xl border bg-background p-3.5 text-left transition-colors hover:border-foreground/30',
    !leeg && dringend && 'border-amber-300 bg-amber-50/60 dark:bg-amber-950/20',
  );
  const inhoud = (
    <>
      <span className={cn('flex items-center gap-1.5 text-xs', leeg ? 'text-muted-foreground' : 'text-foreground/80')}>
        <Icoon className="h-3.5 w-3.5" aria-hidden />
        {label}
      </span>
      <span className={cn('text-2xl font-semibold tabular-nums', leeg && 'text-muted-foreground/60')}>{getal ?? '·'}</span>
      {onder && <span className="truncate text-xs text-muted-foreground">{onder}</span>}
    </>
  );
  /* Een anker op deze pagina: scrollen binnen de scrollcontainer van de layout. */
  if (naar.startsWith('#')) {
    return (
      <button type="button" className={klasse} onClick={() => document.getElementById(naar.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
        {inhoud}
      </button>
    );
  }
  return <Link to={naar} className={klasse}>{inhoud}</Link>;
}

function AandachtRij({ a, opvolg }: { a: AandachtData | undefined; opvolg: { open: number; teLaat: number } | undefined }) {
  return (
    <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
      {/* Stond hier: mail te beoordelen. De triage blijft bereikbaar via de zijbalk; wat aandacht
          vraagt is wat je een relatie hebt toegezegd (ADR 0028). */}
      <Tegel naar="#opvolgen" icoon={PhoneCall} getal={opvolg?.teLaat} label="Opvolgen te laat" dringend
        onder={opvolg ? `${opvolg.open} open` : null} />
      <Tegel naar="/crm/todos" icoon={CheckSquare} getal={a?.takenTeLaat} label="Mijn taken te laat" dringend
        onder={a ? `${a.takenOpen} open` : null} />
      <Tegel naar="#mijlpalen" icoon={Flag} getal={a?.mijlpalenTeLaat} label="Mijlpalen over datum" dringend
        onder={a ? `${a.mijlpalenKomend} in de komende 30 dagen` : null} />
      <Tegel naar="/crm/quotes?tab=verstuurd" icoon={FileText} getal={a?.offertesWachtOpKlant} label="Offertes wacht op klant"
        dringend={Boolean(a?.offertesVerlooptDezeWeek)}
        onder={a ? [`${a.offertesVerlooptDezeWeek} verloopt deze week`, a.offertesVerlopen ? `${a.offertesVerlopen} verlopen` : null].filter(Boolean).join(' · ') : null} />
    </div>
  );
}

/* ── pijplijn ─────────────────────────────────────────────────────── */

function Pijplijn({ projecten }: { projecten: DashboardProject[] }) {
  const lopend = projecten.filter((p) => p.status !== 'verloren' && p.status !== 'afgerond');
  const afgesloten = projecten.length - lopend.length;
  const per = STADIA.map((s) => {
    const l = lopend.filter((p) => p.stage === s);
    return { s, aantal: l.length, waarde: l.reduce((t, p) => t + (p.amount ?? 0), 0) };
  });
  const totaal = per.reduce((t, x) => t + x.aantal, 0) || 1;
  const waarde = per.reduce((t, x) => t + x.waarde, 0);
  return (
    <Blok
      titel="Pijplijn"
      id="dash-pijplijn"
      rechts={<span className="text-sm text-muted-foreground">{lopend.length} lopend · {euroKort(waarde)}</span>}
    >
      <div className="flex flex-col gap-3 rounded-xl border bg-background p-4">
        <div className="flex h-2.5 overflow-hidden rounded-full bg-muted" aria-hidden>
          {per.map((x) => x.aantal > 0 && <span key={x.s} className={STIP[x.s]} style={{ width: `${(x.aantal / totaal) * 100}%` }} />)}
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {per.map((x) => (
            <Link key={x.s} to={`/crm/projects?stage=${x.s}`} className="flex flex-col gap-0.5 rounded-lg px-2 py-1.5 hover:bg-muted">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className={cn('h-2 w-2 rounded-full', STIP[x.s])} aria-hidden />
                {getStageConfig(x.s).label}
              </span>
              <span className="text-lg font-semibold tabular-nums">{x.aantal}</span>
              <span className="text-xs tabular-nums text-muted-foreground">{x.waarde ? euroKort(x.waarde) : 'geen waarde'}</span>
            </Link>
          ))}
        </div>
        {afgesloten > 0 && (
          <Link to="/crm/projects?status=afgesloten" className="self-start text-xs text-muted-foreground hover:text-foreground">
            {afgesloten} afgesloten (verloren of afgerond)
          </Link>
        )}
      </div>
    </Blok>
  );
}

/* ── actieve projecten ────────────────────────────────────────────── */

function Signaalregel({ p, s }: { p: DashboardProject; s: Signalen | undefined }) {
  const delen = [
    p.organization?.name && p.organization.name !== p.name ? p.organization.name : null,
    s?.openTaken ? `${s.openTaken} open ${s.openTaken === 1 ? 'taak' : 'taken'}` : null,
  ].filter(Boolean);
  return (
    <span className="flex min-w-0 items-center gap-1.5 truncate text-xs text-muted-foreground">
      {s?.ems && <span className="inline-flex shrink-0 items-center gap-1 font-medium text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />EMS</span>}
      {s?.ems && delen.length > 0 && <span aria-hidden>·</span>}
      <span className="truncate">{delen.join(' · ')}</span>
    </span>
  );
}

function ActieveProjecten({ projecten, signalen, focus }: { projecten: DashboardProject[]; signalen: Map<string, Signalen>; focus: Set<string> }) {
  const lijst = filterEnSorteer(projecten as unknown as LijstProject[], STANDAARD_FILTER, signalen).slice(0, 8) as unknown as DashboardProject[];
  const focusProjecten = projecten.filter((p) => focus.has(p.id));
  return (
    <Blok titel="Actieve projecten" id="dash-projecten" rechts={<Verder naar="/crm/projects">Alle projecten</Verder>}>
      {focusProjecten.length > 0 && (
        <div className="-mt-1 flex flex-wrap gap-2">
          {focusProjecten.map((p) => (
            <Link key={p.id} to={`/crm/projects/${p.id}`} className="flex h-8 items-center gap-2 rounded-full border bg-background px-3 text-sm hover:border-foreground/40">
              <span className={cn('h-2 w-2 rounded-full', STIP[p.stage ?? ''] ?? 'bg-slate-400')} aria-hidden />
              {p.project_number && <span className="font-mono text-xs text-muted-foreground">{p.project_number}</span>}
              <span className="max-w-[200px] truncate font-medium">{p.name}</span>
            </Link>
          ))}
        </div>
      )}
      <ul className="divide-y rounded-xl border bg-background">
        {lijst.map((p) => {
          const s = signalen.get(p.id);
          const c = getStageConfig(p.stage);
          return (
            <li key={p.id}>
              <Link to={`/crm/projects/${p.id}`} className="grid grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted/50 md:grid-cols-[48px_minmax(0,1fr)_100px_100px_80px]">
                <span className="font-mono text-xs text-muted-foreground">{p.project_number ?? '—'}</span>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-medium">{p.name}</span>
                  <Signaalregel p={p} s={s} />
                </span>
                <span className="hidden md:block"><span className={cn('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', c.bg, c.text)}>{c.label}</span></span>
                <span className="hidden text-right tabular-nums md:block">{euro(p.amount)}</span>
                <span className="text-right text-xs text-muted-foreground">{relatief(activiteit(p as unknown as LijstProject, s))}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </Blok>
  );
}

/* ── recent ───────────────────────────────────────────────────────── */

const RECENT_ICOON = { versie: History, notitie: StickyNote, offerte: FileText, mail: Mail } as const;

function Recent({ items, projectNaam }: { items: RecentItem[]; projectNaam: (id: string) => string }) {
  if (items.length === 0) return null;
  return (
    <Blok titel="Recent in projecten" id="dash-recent">
      <ol className="m-0 flex list-none flex-col divide-y rounded-xl border bg-background p-0">
        {items.map((i) => {
          const Icoon = RECENT_ICOON[i.soort];
          return (
            <li key={i.id}>
              <Link to={`/crm/projects/${i.project_id}`} className="grid grid-cols-[20px_minmax(0,1fr)_auto] items-start gap-3 px-4 py-2.5 text-sm hover:bg-muted/50">
                <Icoon className="mt-0.5 h-4 w-4 text-muted-foreground" aria-hidden />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{i.tekst}</span>
                  <span className="truncate text-xs text-muted-foreground">{projectNaam(i.project_id)}</span>
                </span>
                <span className="whitespace-nowrap text-xs text-muted-foreground">{relatief(i.tijd)}</span>
              </Link>
            </li>
          );
        })}
      </ol>
    </Blok>
  );
}

/* ── rechterkolom ─────────────────────────────────────────────────── */

/** Eén groep van het blok Opvolgen, per project: de eerste acht, de rest op verzoek. */
function OpvolgGroep({ id, titel, uitleg, punten, mijnId, project }: {
  id: string; titel: string; uitleg: string; punten: Opvolgpunt[]; mijnId: string | null; project: (id: string) => ProjectKop | undefined;
}) {
  const [alles, setAlles] = useState(false);
  if (punten.length === 0) return null;
  const lijst = alles ? punten : punten.slice(0, 8);
  const vandaag = opvolgDag(new Date());
  const teLaat = punten.filter((p) => (puntDag(p) ?? vandaag) < vandaag).length;
  return (
    <div aria-labelledby={id}>
      <h3 id={id} className="flex items-baseline justify-between gap-2 px-4 py-2 text-sm font-semibold" title={uitleg}>
        {titel}
        <span className="text-xs font-normal text-muted-foreground">
          {punten.length}{teLaat > 0 && <> · <span className="font-medium text-red-700">{teLaat} te laat</span></>}
        </span>
      </h3>
      <OpvolgLijst punten={lijst} mijnId={mijnId} project={project} />
      {punten.length > 8 && (
        <button type="button" className="w-full border-t px-4 py-2 text-left text-xs text-muted-foreground hover:text-foreground" onClick={() => setAlles(!alles)} aria-expanded={alles}>
          {alles ? 'Toon minder' : `en nog ${punten.length - lijst.length}`}
        </button>
      )}
    </div>
  );
}

/** Wat ik heb verstuurd en waar ik op wacht, en wat ik een relatie heb toegezegd of nog moet beantwoorden,
 *  uit mijn eigen mail (ADR 0028, 0030). Leeg = niet getoond. Hier wordt niets verstuurd. */
export function Opvolgen({ punten, mijnId, project }: { punten: Opvolgpunt[] | undefined; mijnId: string | null; project: (id: string) => ProjectKop | undefined }) {
  if (!punten?.length) return null;
  const { wachtOpReactie, ikBenAanZet } = groepeer(punten);
  return (
    <section id="opvolgen" className="scroll-mt-4">
      <Blok titel="Opvolgen" id="dash-opvolgen" rechts={<span className="text-sm text-muted-foreground">{punten.length} open</span>}>
        <div className="divide-y overflow-hidden rounded-xl border bg-background">
          <OpvolgGroep id="dash-opvolgen-wacht" titel="Wacht op reactie" uitleg="Wat ik heb verstuurd of gevraagd; de bal ligt bij de relatie."
            punten={wachtOpReactie} mijnId={mijnId} project={project} />
          <OpvolgGroep id="dash-opvolgen-ik" titel="Ik ben aan zet" uitleg="Wat ik heb toegezegd en wat een relatie mij vraagt."
            punten={ikBenAanZet} mijnId={mijnId} project={project} />
        </div>
      </Blok>
    </section>
  );
}

function MijnTaken({ taken, projectNaam }: { taken: Taak[] | undefined; projectNaam: (id: string) => string }) {
  const afronden = useRondTaakAf();
  const lijst = (taken ?? []).slice(0, 8);
  return (
    <Blok titel="Mijn taken" id="dash-taken" rechts={<Verder naar="/crm/todos">Alle taken</Verder>}>
      <div className="rounded-xl border bg-background">
        {!taken ? (
          <div className="h-24 animate-pulse" aria-busy />
        ) : lijst.length === 0 ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">Geen open taken.</p>
        ) : (
          <ul className="divide-y">
            {lijst.map((t) => {
              const over = t.due_date ? dagenVerschil(t.due_date) : null;
              return (
                <li key={t.id} className="flex items-start gap-3 px-4 py-2.5 text-sm">
                  <Checkbox id={`dt-${t.id}`} className="mt-0.5" disabled={afronden.isPending} onCheckedChange={() => afronden.mutate(t.id)} aria-label={`${t.title} afronden`} />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <label htmlFor={`dt-${t.id}`} className="cursor-pointer break-words leading-snug">{t.title}</label>
                    <span className="flex flex-wrap gap-x-2 text-xs text-muted-foreground">
                      {over !== null && (
                        <span className={cn(over < 0 && 'font-medium text-red-700')}>
                          {over < 0 ? `${-over} ${-over === 1 ? 'dag' : 'dagen'} te laat` : over === 0 ? 'vandaag' : `over ${over} ${over === 1 ? 'dag' : 'dagen'}`}
                        </span>
                      )}
                      {t.project_id && <Link to={`/crm/projects/${t.project_id}`} className="truncate hover:text-foreground">{projectNaam(t.project_id)}</Link>}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Blok>
  );
}

function Live({ locaties, projectNaam }: { locaties: LiveLocatie[]; projectNaam: (id: string) => string }) {
  if (locaties.length === 0) return null;
  const kw = (v: number | null) => (v === null ? '—' : `${Math.round(v)} kW`);
  return (
    <Blok titel="Live installaties" id="dash-live">
      <ul className="flex flex-col gap-2">
        {locaties.map((l) => (
          <li key={l.id}>
            <Link
              to={l.project_id ? `/crm/projects/${l.project_id}` : `/crm/ems/${l.id}`}
              className="flex flex-col gap-2 rounded-xl bg-[#0b1220] p-4 text-slate-200 hover:ring-2 hover:ring-slate-500"
            >
              <span className="flex items-center justify-between gap-2 text-sm">
                <span className="flex min-w-0 items-center gap-2 font-semibold text-slate-50">
                  <Zap className="h-4 w-4 shrink-0 text-emerald-400" aria-hidden />
                  <span className="truncate">{l.naam}</span>
                </span>
                <span className="shrink-0 text-xs text-slate-400">{l.laatste ? new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit' }).format(new Date(l.laatste)) : 'geen meting'}</span>
              </span>
              <span className="grid grid-cols-3 gap-2 text-center">
                <span className="flex flex-col"><span className="text-[11px] text-slate-400">Verbruik</span><span className="text-base font-semibold tabular-nums text-slate-50">{kw(l.verbruikKw)}</span></span>
                <span className="flex flex-col"><span className="text-[11px] text-slate-400">{l.netKw !== null && l.netKw < 0 ? 'Naar net' : 'Uit net'}</span><span className="text-base font-semibold tabular-nums text-blue-300">{kw(l.netKw === null ? null : Math.abs(l.netKw))}</span></span>
                <span className="flex flex-col"><span className="text-[11px] text-slate-400">Batterij</span><span className="text-base font-semibold tabular-nums text-emerald-300">{l.batterijProcent === null ? '—' : `${Math.round(l.batterijProcent)}%`}</span></span>
              </span>
              {l.project_id && <span className="truncate text-xs text-slate-400">{projectNaam(l.project_id)}</span>}
            </Link>
          </li>
        ))}
      </ul>
    </Blok>
  );
}

function Mijlpalen({ mijlpalen, projectNaam }: { mijlpalen: Mijlpaal[] | undefined; projectNaam: (id: string) => string }) {
  const lijst = (mijlpalen ?? []).slice(0, 6);
  const rest = (mijlpalen?.length ?? 0) - lijst.length;
  return (
    <section id="mijlpalen" className="scroll-mt-4">
      <Blok titel="Mijlpalen" id="dash-mijlpalen">
        <div className="rounded-xl border bg-background">
          {!mijlpalen ? (
            <div className="h-20 animate-pulse" aria-busy />
          ) : lijst.length === 0 ? (
            <p className="px-4 py-3 text-sm text-muted-foreground">Geen open mijlpalen tot 30 dagen vooruit.</p>
          ) : (
            <ul className="divide-y">
              {lijst.map((m) => {
                const d = dagenVerschil(m.milestone_date);
                return (
                  <li key={m.id}>
                    <Link to={`/crm/projects/${m.project_id}`} className="flex flex-col gap-0.5 px-4 py-2.5 text-sm hover:bg-muted/50">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="truncate font-medium">{m.title}</span>
                        <span className={cn('shrink-0 text-xs', d < 0 ? 'font-medium text-red-700' : 'text-muted-foreground')}>
                          {d < 0 ? `${-d} dgn over datum` : DAG_KORT.format(middag(alsDag(m.milestone_date)))}
                        </span>
                      </span>
                      <span className="truncate text-xs text-muted-foreground">{projectNaam(m.project_id)}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
          {rest > 0 && <p className="border-t px-4 py-2 text-xs text-muted-foreground">en nog {rest}</p>}
        </div>
      </Blok>
    </section>
  );
}

function Charging() {
  const c = useCharging();
  if (!c.data) return null;
  const k = c.data.kavels;
  return (
    <Blok titel="Charging" id="dash-charging" rechts={<Verder naar="/crm/charging">Openen</Verder>}>
      <div className="flex flex-col gap-3 rounded-xl border bg-background p-4 text-sm">
        <div className="grid grid-cols-4 gap-2 text-center">
          {([['Acquisitie', k.acquisitie, '/crm/charging/acquisitie'], ['Realisatie', k.realisatie, '/crm/charging/realisatie'], ['Operatie', k.operatie, '/crm/charging/operatie'], ['Vloten', c.data.opportunitiesLopend, '/crm/charging/opportunities']] as const).map(([label, n, naar]) => (
            <Link key={label} to={naar} className="flex flex-col rounded-lg py-1 hover:bg-muted">
              <span className="text-lg font-semibold tabular-nums">{n}</span>
              <span className="text-[11px] text-muted-foreground">{label}</span>
            </Link>
          ))}
        </div>
        {c.data.takenTeLaat > 0 && (
          <Link to="/crm/charging/todos" className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 hover:bg-amber-100 dark:bg-amber-950/30 dark:text-amber-200">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {c.data.takenTeLaat} taken van het Charging-team zijn over datum
          </Link>
        )}
      </div>
    </Blok>
  );
}

/* ── pagina ───────────────────────────────────────────────────────── */

export function Dashboard() {
  const { user, profile } = useAuth();
  const aandacht = useAandacht(user?.id);
  const projecten = useDashboardProjecten();
  const signalen = useSignalen();
  const taken = useMijnTaken(user?.id);
  const mijlpalen = useMijlpalen();
  const recent = useRecent();
  const live = useLive();
  const opvolging = useMijnOpvolging(user?.id);
  const opvolgTelling = useOpvolgTelling(user?.id);
  const { starredIds } = useProjectStars();

  const naamVan = new Map((projecten.data ?? []).map((p) => [p.id, [p.project_number, p.name].filter(Boolean).join(' ')]));
  const projectNaam = (id: string) => naamVan.get(id) ?? 'Project';
  const kopVan = new Map((projecten.data ?? []).map((p) => [p.id, { nummer: p.project_number, naam: p.name, stage: p.stage } satisfies ProjectKop]));
  const projectKop = (id: string) => kopVan.get(id);
  const naam = (profile as { display_name?: string | null } | null)?.display_name || user?.email?.split('@')[0] || '';

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col">
          <h1 className="text-2xl font-semibold tracking-tight">{groet()}{naam ? `, ${naam}` : ''}</h1>
          <p className="text-sm text-muted-foreground">{(() => { const d = DATUM.format(new Date()); return d.charAt(0).toUpperCase() + d.slice(1); })()}</p>
        </div>
        <ProjectZoeker vorm="veld" sneltoets className="sm:w-72" />
      </header>

      <AandachtRij a={aandacht.data} opvolg={opvolgTelling.data} />

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(320px,380px)]">
        <div className="flex min-w-0 flex-col gap-6">
          {projecten.data ? <Pijplijn projecten={projecten.data} /> : <div className="h-40 animate-pulse rounded-xl bg-muted" />}
          {projecten.data && <ActieveProjecten projecten={projecten.data} signalen={signalen.data ?? new Map()} focus={starredIds} />}
          <Recent items={recent.data ?? []} projectNaam={projectNaam} />
        </div>
        <div className="flex min-w-0 flex-col gap-6">
          <Opvolgen punten={opvolging.data} mijnId={user?.id ?? null} project={projectKop} />
          <MijnTaken taken={taken.data} projectNaam={projectNaam} />
          <Live locaties={live.data ?? []} projectNaam={projectNaam} />
          <Mijlpalen mijlpalen={mijlpalen.data} projectNaam={projectNaam} />
          <Charging />
        </div>
      </div>
    </div>
  );
}
